import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { getNameInitials } from '@/utils/affiliatePromotionLinkHelpers';
import { AffiliateRankingListData } from '@/@types/AffiliateReport';

interface Props {
  ranking: AffiliateRankingListData;
  isLoading?: boolean;
  showSecondary?: boolean;
  showTertiary?: boolean;
  valueLabel?: string;
  secondaryLabel?: string;
}

export default function AffiliateRankingList({
  ranking,
  isLoading,
  showSecondary = false,
  showTertiary = false,
  valueLabel,
  secondaryLabel,
}: Props) {
  const maxValue = Math.max(...ranking.entries.map((e) => e.numericValue), 1);

  return (
    <Card className="border border-zinc-800 rounded-lg shadow-lg bg-zinc-950 flex flex-col min-w-0">
      <div className="px-4 py-3 sm:px-5 sm:py-4 border-b border-zinc-800 min-h-[52px] flex items-center">
        {isLoading ? (
          <Skeleton className="h-4 w-3/5 bg-zinc-800" />
        ) : (
          <h3 className="text-white font-semibold text-sm sm:text-base line-clamp-2">
            {ranking.title}
          </h3>
        )}
      </div>

      <div className="px-3 py-3 sm:px-4 sm:py-4 overflow-y-auto max-h-[300px] sm:max-h-[320px]">
        {isLoading ? (
          <div className="space-y-2.5">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-[52px] w-full bg-zinc-800 rounded-lg" />
            ))}
          </div>
        ) : ranking.entries.length === 0 ? (
          <div className="flex items-center justify-center min-h-[200px]">
            <p className="text-sm text-zinc-500 text-center px-4">
              {ranking.emptyMessage}
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {ranking.entries.map((entry, index) => (
              <div key={entry.id + index} className="relative">
                <div
                  className="absolute inset-0 bg-zinc-900 rounded-lg transition-all duration-500"
                  style={{ width: `${(entry.numericValue / maxValue) * 100}%` }}
                />
                <div className="relative flex items-center justify-between gap-2 px-3 py-2.5 rounded-lg bg-zinc-900/50 z-10">
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <span className="text-xs text-zinc-500 w-5 shrink-0 tabular-nums">
                      {index + 1}.
                    </span>
                    <Avatar className="h-7 w-7 sm:h-8 sm:w-8 shrink-0">
                      {entry.avatarUrl && (
                        <AvatarImage src={entry.avatarUrl} alt={entry.name} />
                      )}
                      <AvatarFallback className="bg-zinc-800 text-zinc-300 text-xs">
                        {getNameInitials(entry.name)}
                      </AvatarFallback>
                    </Avatar>
                    <span className="text-white text-xs sm:text-sm font-medium truncate">
                      {entry.name}
                    </span>
                  </div>

                  <div className="text-right shrink-0">
                    {valueLabel && (
                      <div className="text-[10px] text-zinc-500 uppercase tracking-wide">
                        {valueLabel}
                      </div>
                    )}
                    <div className="text-white text-xs sm:text-sm font-semibold tabular-nums">
                      {entry.primaryValue}
                    </div>
                    {showSecondary && entry.secondaryValue && (
                      <>
                        {secondaryLabel && (
                          <div className="text-[10px] text-zinc-500 uppercase tracking-wide mt-0.5">
                            {secondaryLabel}
                          </div>
                        )}
                        <div className="text-zinc-400 text-xs truncate max-w-[100px] sm:max-w-[120px]">
                          {entry.secondaryValue}
                        </div>
                      </>
                    )}
                    {showTertiary && entry.tertiaryValue && (
                      <div className="text-zinc-500 text-xs">{entry.tertiaryValue}</div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}
